/* HAKI community globe + Chatroom (sections/haki-globe.liquid).
 *
 * Founder-approved design: demo v7.1, 2026-10-07 ("sky bold looks great").
 * Dark = the v3 dark palette. Light = "Sky, bold dots" with NO white outline under the arcs.
 *
 * Two parts:
 *   1. HakiGlobeCore: pure functions (no DOM). The stop picker, the bot-town denylist, the
 *      staff filter and the "1 in N" share live here so tests/globe-core.test.js can run them
 *      in Node.
 *   2. The section UI: three.js globe, live activity tour, Chatroom.
 *
 * Everything the globe shows is real: events from /api/presence, and cities from its 30-day
 * rollup. Nothing is invented. User text is only ever set with textContent.
 */
(function (root) {
  'use strict';

  // ===================================================================== core

  /* Probable bot / data-centre towns, kept OFF the globe's city list (dots, tour stops, the
     "1 in N" share). The 30-day visitor TOTAL is not filtered, and the page says so.
     The server-side bot filter (globe-api/src/botfilter.js) covers new data since 2026-10-07;
     this list cleans the rollup rows written before it and can go once they age past 30 days.
     Matched on name AND position (within 0.6 degrees), so Quincy WA is hidden, Quincy MA is not. */
  var DENY_TOWNS = [
    ['San Jose', 37.3, -121.9], ['Prineville', 44.3, -120.8], ['Quincy', 47.2, -119.9],
    ['The Dalles', 45.6, -121.2], ['Boardman', 45.8, -119.7], ['Forest City', 35.3, -81.9],
    ['Council Bluffs', 41.3, -95.9], ['Altoona', 41.6, -93.5], ['Lulea', 65.6, 22.2], ['Luleå', 65.6, 22.2],
    ['Ashburn', 39.0, -77.5], ['Moncks Corner', 33.2, -80.0], ['Pryor', 36.3, -95.3],
    ['Eemshaven', 53.4, 6.8], ['Hamina', 60.6, 27.2], ['Saint-Ghislain', 50.5, 3.8],
    ['Beijing', 39.9, 116.4], ['Shijiazhuang', 38.0, 114.5], ['Jinzhong', 37.7, 112.8],
    ['Singapore', 1.3, 103.8], ['Blanchardstown', 53.4, -6.4], ['Kabul', 34.5, 69.2]
  ];

  /* Staff accounts: their test and launch messages stay out of the Chatroom (founder call,
     2026-10-07). Compared case-insensitively, without the "@". */
  var STAFF_HANDLES = ['devinas12', 'hakistudios_', 'haki.magazine'];

  var US_NAMES = { US: 1, USA: 1, 'United States': 1, 'United States of America': 1 };

  function num(v) { return typeof v === 'number' && isFinite(v); }
  function cityName(label) {
    label = String(label || '');
    var i = label.lastIndexOf(',');
    return (i < 0 ? label : label.slice(0, i)).trim();
  }
  function countryOf(label) {
    label = String(label || '');
    var i = label.lastIndexOf(',');
    var c = (i < 0 ? label : label.slice(i + 1)).trim();
    return US_NAMES[c] ? 'US' : c;
  }
  /* Rough continent from coordinates. Only used to vary the tour, so borders can be loose. */
  function continentOf(lat, lng) {
    if (lat < -10 && lng > 110) return 'OC';
    if (lng > 165 || lng < -150) return lat < 0 ? 'OC' : 'NA'; // Pacific islands, NZ / Alaska, Hawaii
    if (lng < -30) return (lat > 13 || (lat > 7 && lng < -77)) ? 'NA' : 'SA';
    if (lng < 60) {
      if (lat >= 36) return 'EU';
      if (lng > 34 && lat > 12) return 'AS'; // Middle East
      return 'AF';
    }
    return 'AS';
  }
  function km(a, b) {
    var r = Math.PI / 180, dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 12742 * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  function isDeniedTown(loc) {
    if (!loc) return true;
    var name = cityName(loc.label).toLowerCase();
    if (!name) return false;
    for (var i = 0; i < DENY_TOWNS.length; i++) {
      var t = DENY_TOWNS[i];
      if (t[0].toLowerCase() === name && Math.abs(t[1] - loc.lat) <= 0.6 && Math.abs(t[2] - loc.lng) <= 0.6) return true;
    }
    return false;
  }
  /* A stop that names only the US ("United States", "US"): never shown (founder 2026-10-07,
     "we have plenty of places from United States"). globe-api drops these too; this also
     covers a cached or older API answer. */
  function isBareUs(loc) {
    var label = String((loc && loc.label) || '');
    return label.indexOf(',') < 0 && countryOf(label) === 'US';
  }
  function isStaffHandle(h) {
    h = String(h || '').trim().replace(/^@+/, '').toLowerCase();
    return !!h && STAFF_HANDLES.indexOf(h) >= 0;
  }
  function cleanLocations(locs) {
    return (Array.isArray(locs) ? locs : []).filter(function (l) {
      return l && l.label && num(l.lat) && num(l.lng) && (l.count || 0) > 0 && !isDeniedTown(l);
    });
  }
  /* "about 1 in N visitors" are outside the US, from the cleaned 30-day city list.
     null when there is too little data to say anything honest. */
  function outsideUsOneIn(locs) {
    var tot = 0, out = 0;
    cleanLocations(locs).forEach(function (l) {
      tot += l.count;
      if (countryOf(l.label) !== 'US') out += l.count;
    });
    if (tot < 200 || out <= 0) return null;
    var n = tot / out;
    return n < 1.5 ? null : Math.round(n);
  }
  function relTime(iso, nowMs) {
    var t = new Date(iso).getTime();
    if (!isFinite(t)) return '';
    var s = (t - (nowMs || Date.now())) / 1000, a = Math.abs(s);
    if (a < 60) return 'just now';
    var units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (var i = 0; i < units.length; i++) {
      if (a >= units[i][1]) {
        var v = Math.round(s / units[i][1]);
        if (typeof Intl !== 'undefined' && Intl.RelativeTimeFormat) {
          return new Intl.RelativeTimeFormat('en', { numeric: 'always' }).format(v, units[i][0]);
        }
        var n = Math.abs(v);
        return n + ' ' + units[i][0] + (n === 1 ? '' : 's') + ' ago';
      }
    }
    return '';
  }

  /* Stop picker (round 3, 2026-10-07: "it's all just big cities ... over and over").
     Candidates are real recent events plus EVERY kept 30-day city; nothing is invented.
     1. Live events, when queued, are used about half the time (else the 30-day list).
        An event stop prefers one >= SPREAD km from the last 2 stops; queued events within MERGE km
        fold into it ("X area, N events").
     2. A 30-day stop is a weighted random draw by sqrt(visitors), so small towns come up regularly:
        - no city repeats within the last REPEAT stops (40, or 40% of the list if it is smaller);
        - >= SPREAD km from the last 2 stops when possible;
        - a light nudge: if none of the last 6 stops was outside the US, draw from outside the US.
        It is shown as "Visited in the last 30 days", never as a page view, cart or purchase. */
  function createPicker(opts) {
    opts = opts || {};
    var SPREAD = opts.spreadKm || 1500, MERGE = opts.mergeKm || 160, QMAX = opts.queueMax || 24;
    var EVENT_SHARE = opts.eventShare == null ? 0.5 : opts.eventShare;
    var queue = [], pool = [], history = [], seq = 0;

    function tag(o) { o.cc = countryOf(o.label); o.cont = continentOf(o.lat, o.lng); return o; }
    function setPool(locs) {
      pool = cleanLocations(locs).filter(function (l) { return !isBareUs(l); }).map(function (l) {
        var o = tag({ label: l.label, lat: l.lat, lng: l.lng, count: l.count, level: l.level || null });
        o.w = Math.sqrt(l.count);
        return o;
      });
    }
    function pushEvents(evs) {
      (evs || []).forEach(function (e) {
        if (!e || !num(e.lat) || !num(e.lng) || !e.label || isDeniedTown(e) || isBareUs(e)) return;
        queue.push(tag({ id: e.id, label: e.label, lat: e.lat, lng: e.lng, type: e.type, at: e.at || null, level: e.level || null, n: seq++ }));
      });
      while (queue.length > QMAX) queue.shift();
    }
    function lastN(n) { return n > 0 ? history.slice(-n) : []; } // slice(-0) would return everything
    function farFromLast2(p) {
      var l2 = lastN(2);
      for (var i = 0; i < l2.length; i++) if (km(p, l2[i]) < SPREAD) return false;
      return true;
    }
    function drawWeighted(list, rnd) {
      var tot = 0, i;
      for (i = 0; i < list.length; i++) tot += list[i].w;
      var r = rnd() * tot;
      for (i = 0; i < list.length; i++) { r -= list[i].w; if (r <= 0) return list[i]; }
      return list[list.length - 1];
    }
    function poolPick(rnd) {
      if (!pool.length) return null;
      var repeatWindow = Math.min(40, Math.floor(pool.length * 0.4));
      var recent = {};
      if (repeatWindow > 0) lastN(repeatWindow).forEach(function (h) { recent[h.label] = 1; });
      var cands = pool.filter(function (l) { return !recent[l.label]; });
      if (!cands.length) cands = pool.slice();
      var noAbroad = !lastN(6).some(function (h) { return h.cc !== 'US'; });
      if (noAbroad) {
        var abroad = cands.filter(function (l) { return l.cc !== 'US'; });
        if (abroad.length) cands = abroad;
      }
      var far = cands.filter(farFromLast2);
      return drawWeighted(far.length ? far : cands, rnd);
    }
    function eventPick() {
      if (!queue.length) return null;
      var far = queue.filter(farFromLast2);
      var list = far.length ? far : queue;
      return list.reduce(function (a, e) { return e.n < a.n ? e : a; }, list[0]); // oldest first
    }
    function next(rnd) {
      rnd = rnd || Math.random;
      var useEvent = queue.length && (!pool.length || rnd() < EVENT_SHARE);
      var pick = useEvent ? eventPick() : poolPick(rnd);
      if (!pick) pick = useEvent ? poolPick(rnd) : eventPick();
      if (!pick) return null;
      var stop;
      if (pick.n === undefined) {
        stop = { kind: 'recent', label: pick.label, lat: pick.lat, lng: pick.lng, type: 'recent', count: 1, at: null, cc: pick.cc, cont: pick.cont, level: pick.level };
      } else {
        // A region event ("Ohio, US") is no finer than its region: it never joins a city's
        // "X area" group, and a region stop only groups events of the same region.
        var pickRegion = pick.level === 'region';
        var group = queue.filter(function (e) {
          if (e === pick) return true;
          if (pickRegion) return e.level === 'region' && e.label === pick.label;
          return e.level !== 'region' && km(e, pick) <= MERGE;
        });
        queue = queue.filter(function (e) { return group.indexOf(e) < 0; });
        var rank = { page_view: 0, add_to_cart: 1, purchase: 2 };
        var top = group.reduce(function (a, e) { return (rank[e.type] || 0) > (rank[a.type] || 0) ? e : a; }, pick);
        var newest = group.reduce(function (a, e) { return (e.at || '') > (a.at || '') ? e : a; }, pick);
        var sameType = group.every(function (e) { return e.type === group[0].type; });
        // "Miami area, US" folds nearby city events. A region stop ("Ohio, US", globe-api level
        // 'region') is already an area: it keeps its own label. Older API answers carry no level.
        var isCity = pick.level ? pick.level === 'city' : pick.label.indexOf(',') >= 0;
        stop = {
          kind: 'event', label: pick.label, lat: pick.lat, lng: pick.lng, type: top.type,
          count: group.length, sameType: sameType, at: newest.at, cc: pick.cc, cont: pick.cont, level: pick.level,
          area: group.length > 1 && isCity ? cityName(pick.label) + ' area, ' + pick.cc : null
        };
      }
      history.push(stop); if (history.length > 60) history.shift();
      return stop;
    }
    return {
      setPool: setPool, pushEvents: pushEvents, next: next,
      queueSize: function () { return queue.length; }, poolSize: function () { return pool.length; }
    };
  }

  var Core = {
    DENY_TOWNS: DENY_TOWNS, STAFF_HANDLES: STAFF_HANDLES,
    cityName: cityName, countryOf: countryOf, continentOf: continentOf, km: km,
    isDeniedTown: isDeniedTown, isBareUs: isBareUs, isStaffHandle: isStaffHandle, cleanLocations: cleanLocations,
    outsideUsOneIn: outsideUsOneIn, relTime: relTime, createPicker: createPicker
  };
  root.HakiGlobeCore = Core;
  if (typeof document === 'undefined') return;

  // ======================================================================= UI

  var MASK_B64 = 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA+P8PAOD//x8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD8//+H/////39gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAODB/n/4//////8PAAAAAAAAAAAAAAAAAB4AAAAAAAAAAAAAAAAAAAAAAAAAAOD//wP4//////8BAAAAdRgAAAAAAAAAAPgAAAAAAAAAAAAAAAAAAAAAAAAA4ADv/8H//////38AAAAAcgAAAAAAAAAAAAAeAAAAAAAAAAAAAAAAAAAAAAAAAACgP8D///////8AAAAAPAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAADAGID//////38AAAAAAAAAAAAAwAAAAAB+AAAAAAAAAAAAAAAAAAAAAICPAyMHBwAA/P////8AAAAAAAAAAACAAwAAAP//HwAAwAsAAAAAAAAAAAAAAAAAAAAAAAAA+P////8AAAAAAAAAAABwAAAA+P//AwAAAAAAAAAAAAAAAAAAAP8AAOMhAAAA8P///z8AAAAAAAAAAAAcAAAA/f9/DAAAAAAAAAAAAAAAAAAAgN9PhyeYxwAA4P///zcAAAAAAAAAAAAOAA7g//////8fAAIAAAAAAAAAAAAAAMP/ByK4/wMAgP///x8AAAAAAAAAAAAPAE+c//////8fAP8DAAAAAADgDwAAAAD7D3Dw/z8AAP7//wMAAAAAAPABAAAAgN//////////////BwAAAAD+/z8Anwb/P8SAg/8AQP7//w8AAAAAwP8XAAAAAt//////////////D4APAMD///////8BAMyNA/wAAP7//wAAAAAA8P//AQBCfp79////////////////DwD///////9zv+/Pg/EPgP//BwAAAAAA+P//H+H//4//////////////////3wH4//////////+/APg/gP//AQAAAAAA/v8/D/7//+f/////////////////8OD///////////9fAPwYgP8fAEA/AAAA/uN/8P//////////////////////QAD4///////////Pgf0DAP8PAMAfAACA/+F/8P////////////////////8/AAD4//////////8BAPAHAP4HAAAAAADAf/j///////////////////////9/AID///////////8AAIABAPwDAAAAAAD4P/z/////////////////////f/4fAMD//////////z8AwA8AAPgDAAAAAAD+H/z/////////////////////Mf8BAID/7/j//////x8AwD8AAMABAAAAAAD+Pzz+//////////////////9/gEcAAAD8AwD+/////x8AwD8IAAAAAAAAAAD+fwD+//////////////////8xYAAAAACAAwD4/////z8AwP8cAAAAAAAAAACcH/D//////////////////wEAMAAAAADABACQ/////38AgP8/AAAAAAAAwAEAD+D//////////////////wAAfgAAAAAgAADA//////8HAP9/AAAAAAAAwAEwDv7/////////////////PwAAfwAAAAAAAACA//////8fgP//AAAAAAAAgAMgAP7/////////////////DwAAPwAAAAAAAAAA/P//////4f//AwAAAAAAMAYgAP//////////////////DwAADwAAAAAAAACA/P//////4///DwAAAAAAPA7g/////////////////////wEADwAAAAAAAAAA+P//////4///DwAAAAAAOB/8/////////////////////wUAQgAAAAAAAAAA8P//////5///CwAAAAAAAB///////////////////////wUAAAAAAAAAAAAA8P//////////CAAAAAAAAMD//////////////////////wwAAAAAAAAAAAAAQP////////8BDAAAAAAAAOD//////////////////////wwABAAAAAAAAAAAAP7//////38HPAAAAAAAAPz//////w/+/////////////wQAAgAAAAAAAAAAAP////////8HQAAAAAAAAPz//////wP8////////////fwAAgAAAAAAAAAAAAP////////8HAAAAAAAAAPz///93+AH4////////////PwBAAFAAAAAAAAAAAP////////9/AAAAAAAAAPj//f9j/AH4////////////HwAgAAAAAAAAAAAAAP////////8MAAAAAAAAAPj/+P8B+AP8////////////DwQYAAAAIAAAAAAAAP///////z8AAAAAAAAAAPiG8f8A8Af/////////////BxwGAAAAIAAAAAAAAP///////x8AAAAAAAAA+H+Ag/8AwA/+////3/////9/AAQBAAAAAAAAAAAAAP///////x8AAAAAAAAA+D8ADv/wwB/4//////////8/AAAAAAAAAAAAAAAAAP///////wMAAAAAAAAA8A8gGE/+/z/4/////////98fAAEAAAAAAAAAAAAAAP///////wMAAAAAAAAA+A8gEMf//w/8/////////0cOAAMAAMAAAAAAAAAAAP7//////wEAAAAAAAAA+A8AAIb//x/8/////////wMOABEAAAAAAAAAAAAAAPz/////fwAAAAAAAAAA+AMABIT//x/8/////////wcYgAEAAAAAAAAAAAAAAPz//////wAAAAAAAAAAQIA/AAAy/3/8/////////x8Y4AkCAAAAAAAAAAAAAPj//////wAAAAAAAAAAAPh/AAAA/////////////w8Y8AAOAAAAgAAAAAAAAOD/////PwAAAAAAAAAAwP87AAAA/////////////wcAGwQAAAAAAAAAAAAAAMD/////HwAAAAAAAAAA4P9/AAAA/////////////x8AAAQBAAAAAAAAAAAAAID/////DwAAAAAAAAAA+P//AwaA/////////////x/AAIQAAAAAAAAAAAAAAAD5////BwAAAAAAAAAA+P//Dx+A/////////////z8AAAQAQAAAAAAAAAAAAAD5////AwAAAAAAAAAA+P//P////////////////x8AAAQAAAQAAAAAAAAAAADy/x8CBgAAAAAAAAAA/P///z///4///////////z8AAEQAAAAAAAAAAAAAAADk/wcABgAAAgAAAAAA/v/////f/4///////////x8AAAgAAAAAAAAAAAAAAADE/wcABAAAAAAAAACA//////8//x/+/////////x8AAAiAEoACAAAAAAAAAACI/wcADAAAAAAAAACA//////8//z/g/////////w8AAAAAAAAAAAAAAAAAAACQ/wcAAAAAAgAAAADA//////9//j8Av////////wcAAAAAAAgAAAAAAAAAAAAQ/wMAAAAAAAAAAADg//////9//H8IgP///////yMAAAAAABgAAAAAAAAAAAAA/AMAAAAAAgAAAADw////////+P8/AP///////zEAAAAAAAAAAAAAAAAAAAAA/AMADAAAAAAAAADw////////+P9/APz/f///PwAAAGAAAAAAAAAAAAAAAAAA+AMAYAAAAAAAAAD4////////+f9/AOz/B///BAAAAAAAAAAAAAAAAAAAAAAA+AccgAEAAAAAAADw////////8f8/AOD/B/4/AAAAAAAAAAAAAAAAAQAAAAAA+AcMADAvAAAAAADw////////4f8/AOD/Afw/BgAAAAAAAAAAAAAAAAAAAAAA4B8OADAAAAAAAADw////////w/8fAOD/APg/AgAAAAAAAAAAAAAAAAAAAAAAgP8PAAAAAAAAAADw////////x/8HAOB/APg/ACAAAAABAAAAAAAAAAAAAAAAAPwHAAAAAAAAAADw////////h/8BAMA/AOh/ACAAAAAAAAAAAAAAAAAAAAAAAID/AAAAAAAAAADw////////j/8AAMAPAMD/ARAAAAAAAAAAAAAAAAAAAAAAAAD/AQAAAAAAAAD4////////jx8AAIAPAMD/ASAAAAAYAAAAAAAAAAAAAAAAAAD4AAAAAAAAAADw////////rwMAAIAPAMD+AQAAAECAAAAAAAAAAAAAAAAAAADgAAAAAAAAAADw////////fwAAAIAPAID8AQAAACAAAAAAAAAAAAAAAAAAAADAAAgAAAAAAADg////////fwAAAAAPAID4AQAEAAgAAAAAAAAAAAAAAAAAAADAAP4AAAAAAADA/////////3gAAAAPAABwAAAAAAAAAACAAAAAAAAAAAAAAAAAAe9/AAAAAACA/////////38AAAAGAAAgAAAIAAAAAAAAAAAAAAAAAAAAAAAAjv9/AAAAAACA/////////z8AAAASAIAAAAAKAAAAAAAAAAAAAAAAAAAAAAAAwP//AQAAAAAA/////////z8AAAAwAIAAAAALAAAAAAAAAAAAAAAAAAAAAAAAgP//AwAAAAAA/n///////x8AAAAwAAABAAAJAAAAAAAAAAAAAAAAAAAAAAAAgP//PwAAAAAA+Af8/////x8AAAAAAAAGAAMAAAAAAAAAAAAAAAAAAAAAAAAAgP///wAAAAAAAADk/////w8AAAAAADAGgAMAAAAAAAAAAAAAAAAAAAAAAAAAgP///wEAAAAAAADA/////wcAAAAAAGAGwAEAAAAAAAAAAAAAAAAAAAAAAAAAwP///wEAAAAAAADA/////wMAAAAAAMAM8AMAAAAAAAAAAAAAAAAAAAAAAAAA4P///wMAAAAAAADA/////wAAAAAAAIAD+AMAAAAAAAAAAAAAAAAAAAAAAAAA8P///wMAAAAAAADA////fwAAAAAAAIAH/gMAAAAAAAAAAAAAAAAAAAAAAAAA8P///wcAAAAAAADg////PwAAAAAAAAAH/gEAAAAAAAAAAAAAAAAAAAAAAAAA+P///z8AAAAAAADg////PwAAAAAAAAAO/DEAAwAAAAAAAAAAAAAAAAAAAAAA8P///38AAAAAAADA////DwAAAAAAAAAe/DgA4gAAAAAADAAAAAAAAAAAAAAA8P////8fAAAAAACA////DwAAAAAAAAA8gCgA9gcAAAAAAAAAAAAAAAAAAAAA+P////8/AAAAAAAA////BwAAAAAAAAA4AEAA8B8AAAAAAAAAAAAAAAAAAAAA+P//////AAAAAAAA////BwAAAAAAAAAwAAAAwD8IAAAAAAAAAAAAAAAAAAAA8P//////AQAAAAAA/v//BwAAAAAAAADAAAAAwH8AAAAAAAAAAAAAAAAAAAAA4P//////AQAAAAAA/v//BwAAAAAAAAAAHwAAwH8AAAAAAAAAAAAAAAAAAAAA4P//////AQAAAAAA/P//BwAAAAAAAAAAAAAAAIcAAAAAAAAAAAAAAAAAAAAAwP//////AAAAAAAA/v//DwAAAAAAAAAAAAABAIABAAAAAAAAAAAAAAAAAAAAwP////9/AAAAAAAA/P//DwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgP////8/AAAAAAAA/P//DwAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAgP////8/AAAAAAAA/P//DwAAAAAAAAAAAAAAHwQAAAAAAAAAAAAAAAAAAAAAAP////8fAAAAAAAA/v//DyAAAAAAAAAAAADAHwQAAAAAAAAAAAAAAAAAAAAAAP////8fAAAAAAAA/v//HzAAAAAAAAAAAADMHwwAAAAAAAAAAAAAAAAAAAAAAP7///8fAAAAAAAA////DzgAAAAAAAAAAAD+HxwAAAAAAAAAAAAAAAAAAAAAAPj///8fAAAAAAAA////Dz4AAAAAAAAOAAD/Px4AAAAAAAAAAAAAAAAAAAAAAOD///8fAAAAAAAA////Ax8AAAAAAAAAAMD//z4AAAAAAAAAAAAAAAAAAAAAAMD///8PAAAAAAAA////AR8AAAAAAAAAAMD//z8AAAAAgAAAAAAAAAAAAAAAAMD///8PAAAAAAAA/v9/AB4AAAAAAAAAAMD//38AAAAAAAAAAAAAAAAAAAAAAMD///8PAAAAAAAA/P9/AB4AAAAAAAAAAPj///8BAAAAQAAAAAAAAAAAAAAAAMD///8HAAAAAAAA/P9/AA8AAAAAAAAAAP////8BAAAAQAAAAAAAAAAAAAAAAMD///8DAAAAAAAA+P9/gA8AAAAAAAAAwP////8DAAAAIAAAAAAAAAAAAAAAAMD//38AAAAAAAAA+P9/AA8AAAAAAAAAwP////8HAAAAIAAAAAAAAAAAAAAAAMD//x8AAAAAAAAA+P9/AAcAAAAAAAAAwP////8PAAAAAAAAAAAAAAAAAAAAAMD//w8AAAAAAAAA+P8fAAAAAAAAAAAAwP////8fAAAAAAAAAAAAAAAAAAAAAMD//wcAAAAAAAAA+P8fAAAAAAAAAAAAwP////8fAAAAEAAAAAAAAAAAAAAAAOD//wcAAAAAAAAA8P8fAAAAAAAAAAAAwP////8fAAAAEAAAAAAAAAAAAAAAAOD//wcAAAAAAAAA8P8PAAAAAAAAAAAAgP////8fAAAAAAAAAAAAAAAAAAAAAOD//wMAAAAAAAAA4P8HAAAAAAAAAAAAgP////8fAAAACAAAAAAAAAAAAAAAAOD//wEAAAAAAAAAwP8HAAAAAAAAAAAAgP////8fAAAACAAAAAAAAAAAAAAAAOD//wAAAAAAAAAAwP8DAAAAAAAAAAAAAP////8fAAAABAAAAAAAAAAAAAAAAOD//wAAAAAAAAAAgP8AAAAAAAAAAAAAAP8B/P8PAAAABAAAAAAAAAAAAAAAAOD/fwAAAAAAAAAAgH8AAAAAAAAAAAAAAP8A2P8HAAAABgAAAAAAAAAAAAAAAPD/PwAAAAAAAAAAAAEAAAAAAAAAAAAAAAcAwP8HAAAAAgAAAAAAAAAAAAAAAPD/BwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgP8DAAAAAAAAAAAAAAAAAAAAAPj/BwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP8DAAAAAAAAAAAAAAAAAAAAAPj/BwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP8DAAAIAAAAAAAAAAAAAAAAAPj/AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEgAAAA4AAAAAAAAAAAAAAAAAPg/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAcAAAAAAAAAAAAAAAAAPw/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAAAAAAAAAAAAAAAPwHAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOAAAAADAAAAAAAAAAAAAAAAAPgHAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMAAAIABAAAAAAAAAAAAAAAAAPgHAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMABAAAAAAAAAAAAAAAAAPwDAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHAAAAAAAAAAAAAAAAAAAPwBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHgAAAAAAAAAAAAAAAAAAP4AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPwDAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP4BAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP4AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAH4AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAH4AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB8AAAAAAAAAAAAAAAAAIAPAAAAAAAeOAAAAgAAAAAAAAAAAAAAAAAAAAAAAAB+AAAAAAAAAAAAAAAAAOB/AAAA////f////w8AAAAAAAAAAAAAAAAAAAAAAAB+AAAAAAAAAAAAAAAAwP///wP4/////////38AAAAAAAAAAAAAAAAAAAAAADB/AAAAAAAAAAAAAABg8P///wH+////////////AwAAAAAAAAAAAAAAAAAAAPh/AAAAAAAAAAAgFAD4/////+H/////////////HwAAAAAAAAAAAAAAAAAAAPz/AAAAAAAANOL///////////n//////////////x8AAAAAAAAAAAAA4B8AAPj/AAAAAADg/////////////////////////////z8AAAAAAAAAAAAA4P9/8P9/AAAAAAD//////////////////////////////wcAAAAAAAAA8P8fwP////9/AAAAAPj//////////////////////////////wEAAAAAAID/////////////AQAAAPj/////////////////////////////fwAAAAAAgP//////////////PwAAAP7/////////////////////////////fwAAAADA/////////////////wcA+P///////////////////////////////8AB/3/8//////////////////+N////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////';

  function init(sectionEl) {
    if (sectionEl.__hgv2) return;
    sectionEl.__hgv2 = true;
    var cfgEl = sectionEl.querySelector('script[data-hgv2-config]');
    var CFG = {};
    try { CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { CFG = {}; }
    var T = CFG.strings || {};
    var API = String(CFG.apiUrl || '').replace(/\/+$/, '');
    var $ = function (sel) { return sectionEl.querySelector(sel); };
    var docEl = document.documentElement;

    var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    var mqDark = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : { matches: false };
    var reduced = !!mqReduce.matches;

    // ---------------------------------------------------------- theme (light / dark)
    var THEME_KEY = 'haki-globe-theme'; // the globe's own key, as before
    var theme = null;
    try { var saved = localStorage.getItem(THEME_KEY); if (saved === 'light' || saved === 'dark') theme = saved; } catch (e) {}
    if (!theme) theme = mqDark.matches ? 'dark' : 'light';
    var themeBtn = $('[data-hgv2-theme]');
    function syncPageBg() {
      // Body + #MainContent carry the section's ground so the iOS safe areas and overscroll match.
      var bg = getComputedStyle(sectionEl).getPropertyValue('--hg-bg-solid').trim();
      if (!bg) return;
      document.body.style.backgroundColor = bg;
      var mc = document.getElementById('MainContent');
      if (mc) mc.style.backgroundColor = bg;
    }
    function setThemeClass(t) {
      theme = t;
      sectionEl.classList.remove('is-light', 'is-dark');
      sectionEl.classList.add('is-' + t);
      if (themeBtn) {
        themeBtn.setAttribute('aria-label', t === 'light' ? (T.toDark || 'Switch to dark mode') : (T.toLight || 'Switch to light mode'));
      }
      syncPageBg();
    }
    setThemeClass(theme);
    if (themeBtn) {
      themeBtn.addEventListener('click', function () {
        var nextTheme = theme === 'light' ? 'dark' : 'light';
        var flip = function () { setThemeClass(nextTheme); applyGlobeTheme(); };
        // The house fade (snippets/haki-shade-switch.liquid).
        if (window.HakiShadeSwitch) window.HakiShadeSwitch(flip); else flip();
        try { localStorage.setItem(THEME_KEY, nextTheme); } catch (e) {}
      });
    }
    if (mqDark.addEventListener) {
      mqDark.addEventListener('change', function (e) {
        var stored = null;
        try { stored = localStorage.getItem(THEME_KEY); } catch (err) {}
        if (stored) return; // an explicit choice wins over the system setting
        setThemeClass(e.matches ? 'dark' : 'light');
        applyGlobeTheme();
      });
    }

    // ------------------------------------------------- 30-day total ("● N MEMBERS")
    var totalEl = $('[data-hgv2-total]');
    function renderTotals(d) {
      var rec = d && d.recent;
      if (totalEl && rec && num(rec.total_30d) && rec.total_30d > 0) {
        totalEl.querySelector('[data-hgv2-total-num]').textContent = Number(rec.total_30d).toLocaleString('en-US');
        totalEl.classList.remove('is-pending'); // the block's size was reserved, so nothing moves
      }
    }

    // ------------------------------------------------------------------- Chatroom
    initChat();

    // --------------------------------------------------------------------- globe
    var stage = $('[data-hgv2-stage]');
    var canvasHost = $('[data-hgv2-canvas]');
    var THREE = root.THREE;
    var webglOk = !!(THREE && stage && canvasHost);
    var renderer = null;
    if (webglOk) {
      try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); } catch (e) { webglOk = false; }
    }
    if (!webglOk) {
      if (stage) stage.classList.add('is-nogl');
      var noglMsg = $('[data-hgv2-nogl]');
      if (noglMsg) noglMsg.hidden = false;
      var ctl = $('[data-hgv2-play]');
      if (ctl) ctl.hidden = true;
    }

    var loaderEl = $('[data-hgv2-loader]');
    function hideLoader() {
      if (loaderEl && !loaderEl.hidden) { loaderEl.hidden = true; if (stage) stage.setAttribute('aria-busy', 'false'); }
    }
    if (!webglOk) hideLoader();
    var announcer = $('[data-hgv2-announcer]');
    var liveBadge = $('[data-hgv2-live]');
    var liveCount = $('[data-hgv2-live-count]');
    var pulseEl = $('[data-hgv2-pulse]');
    var card = $('[data-hgv2-card]');
    var cardCity = $('[data-hgv2-card-city]');
    var playBtn = $('[data-hgv2-play]');

    var picker = createPicker({ spreadKm: 1500, mergeKm: 160 });
    var seenEvents = {};
    var firstBatch = true;

    function stopLabel(stop) { return stop.area || stop.label; }
    var lastAnnounce = -1e9;
    function announce(stop) {
      if (!announcer || stop.kind !== 'event') return;
      var t = performance.now();
      if (t - lastAnnounce < (stop.type === 'purchase' ? 8000 : 20000)) return;
      lastAnnounce = t;
      // The card shows the city only (founder 2026-10-07); spoken alone it has no context, so say what it is.
      announcer.textContent = String(T.activityIn || 'Activity in [city]').replace('[city]', stopLabel(stop));
    }

    function ingestPresence(d) {
      if (!d) return;
      if (liveCount) liveCount.textContent = String(d.total || 0);
      if (liveBadge) liveBadge.classList.remove('is-pending');
      renderTotals(d);
      if (d.recent) {
        picker.setPool(d.recent.locations);
        if (cityPoints) drawCityDots(d.recent.locations);
      }
      var evs = Array.isArray(d.events) ? d.events.filter(function (e) {
        return e && e.id && /^(page_view|add_to_cart|purchase)$/.test(e.type) && num(e.lat) && num(e.lng);
      }) : [];
      // First load: the newest few, played oldest to newest. Later: every new one.
      var fresh = (firstBatch ? evs.slice(0, 8) : evs).filter(function (e) { return !seenEvents[e.id]; });
      evs.forEach(function (e) { seenEvents[e.id] = true; });
      picker.pushEvents(fresh.reverse());
      if (firstBatch) {
        firstBatch = false;
        startTour();
      }
    }

    function poll() {
      if (!API || document.hidden) return;
      fetch(API + '/api/presence')
        .then(function (r) { return r.json(); })
        .then(ingestPresence)
        .catch(function () { /* decorative: fail silent */ });
    }
    if (API) {
      poll();
      setInterval(poll, 15000);
      document.addEventListener('visibilitychange', function () { if (!document.hidden) poll(); });
    }

    // ------------------------------------------------------------- three.js scene
    var scene, camera, globe, sphereMat, landGeo, landMat, cityMat, cityGeo, cityPoints = null, colors;
    var R = 1, W = 1, H = 1, DOT_TEX, RING_TEX;
    function tok(name) { return getComputedStyle(sectionEl).getPropertyValue(name).trim(); }
    function themeColors() {
      return {
        ocean: new THREE.Color(tok('--hg-ocean')), land: new THREE.Color(tok('--hg-land')), landDim: new THREE.Color(tok('--hg-land-dim')),
        city: new THREE.Color(tok('--hg-city')),
        page_view: new THREE.Color(tok('--hg-ev-view')), add_to_cart: new THREE.Color(tok('--hg-ev-cart')),
        purchase: new THREE.Color(tok('--hg-ev-buy')), recent: new THREE.Color(tok('--hg-ev-view')),
        dotScale: parseFloat(tok('--hg-dot-scale')) || 1, arcWidth: parseFloat(tok('--hg-arc-width')) || 1
      };
    }
    function ll2v(lat, lng, r) {
      var phi = lat * Math.PI / 180, th = lng * Math.PI / 180; r = r || R;
      return new THREE.Vector3(r * Math.cos(phi) * Math.sin(th), r * Math.sin(phi), r * Math.cos(phi) * Math.cos(th));
    }
    function circleTex(ring) {
      var c = document.createElement('canvas'); c.width = c.height = ring ? 128 : 64;
      var g = c.getContext('2d');
      if (ring) { g.strokeStyle = '#fff'; g.lineWidth = 7; g.beginPath(); g.arc(64, 64, 56, 0, Math.PI * 2); g.stroke(); }
      else { g.fillStyle = '#fff'; g.beginPath(); g.arc(32, 32, 28, 0, Math.PI * 2); g.fill(); }
      return new THREE.CanvasTexture(c);
    }
    function syncDotSize() {
      if (!landMat || !colors) return;
      // Light enlarges land dots; a bit more on small stages, where tiny dots blur away.
      landMat.size = 0.0125 * colors.dotScale * (colors.dotScale > 1 && H < 500 ? 1.3 : 1);
    }
    function buildScene() {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      canvasHost.appendChild(renderer.domElement);
      renderer.domElement.setAttribute('aria-hidden', 'true');
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
      globe = new THREE.Group(); scene.add(globe);
      DOT_TEX = circleTex(false); RING_TEX = circleTex(true);
      colors = themeColors();
      sphereMat = new THREE.MeshBasicMaterial({ color: colors.ocean });
      globe.add(new THREE.Mesh(new THREE.SphereGeometry(R * 0.995, 64, 48), sphereMat));
      // land dots from a 1-degree land mask
      var bin = atob(MASK_B64), mask = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) mask[i] = bin.charCodeAt(i);
      function isLand(lat, lng) {
        var x = Math.floor(lng + 180) % 360, y = Math.floor(90 - lat); if (y < 0) y = 0; if (y > 179) y = 179;
        var k = y * 360 + x; return (mask[k >> 3] >> (k & 7)) & 1;
      }
      var N = 42000, gold = Math.PI * (3 - Math.sqrt(5)), pos = [], tvals = [];
      for (var j = 0; j < N; j++) {
        var y = 1 - (j / (N - 1)) * 2, lat = Math.asin(y) * 180 / Math.PI, lng = ((gold * j) * 180 / Math.PI) % 360 - 180;
        if (!isLand(lat, lng)) continue;
        var v = ll2v(lat, lng, R * 1.001); pos.push(v.x, v.y, v.z); tvals.push(Math.abs(y));
      }
      landGeo = new THREE.BufferGeometry();
      landGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      landGeo.setAttribute('color', new THREE.Float32BufferAttribute(new Array(pos.length).fill(1), 3));
      landGeo.userData.t = tvals;
      landMat = new THREE.PointsMaterial({ size: 0.0125, map: DOT_TEX, alphaTest: 0.5, vertexColors: true, sizeAttenuation: true });
      globe.add(new THREE.Points(landGeo, landMat));
      // every real 30-day city as a faint dot (stronger with reduced motion, where they are the content)
      // 30-day visitor cities: ONE Points geometry (one draw call for every city). Size and opacity
      // follow sqrt(visitors) within a tight range; a soft edge, no per-dot animation. Points on the
      // far side fail the depth test against the opaque sphere, so they stay hidden.
      cityGeo = new THREE.BufferGeometry();
      cityMat = new THREE.ShaderMaterial({
        uniforms: { uColor: { value: colors.city.clone() }, uPx: { value: renderer.getPixelRatio() }, uScale: { value: 1 } },
        vertexShader: 'attribute float aSize; attribute float aAlpha; varying float vA; uniform float uPx; uniform float uScale;' +
          'void main() { vA = aAlpha; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_PointSize = aSize * uPx * uScale; }',
        fragmentShader: 'uniform vec3 uColor; varying float vA;' +
          'void main() { float d = length(gl_PointCoord - 0.5) * 2.0; if (d > 1.0) discard;' +
          ' float a = smoothstep(1.0, 0.45, d) * 0.65 + smoothstep(0.5, 0.3, d) * 0.35; gl_FragColor = vec4(uColor, a * vA); }',
        transparent: true, depthWrite: false
      });
      cityPoints = new THREE.Points(cityGeo, cityMat);
      globe.add(cityPoints);
      applyGlobeTheme();
      resize();
    }
    function drawCityDots(locs) {
      var cp = [];
      cleanLocations(locs).forEach(function (c) { var v = ll2v(c.lat, c.lng, R * 1.004); cp.push(v.x, v.y, v.z); });
      var kept = cleanLocations(locs), max = 1;
      kept.forEach(function (c) { max = Math.max(max, c.count); });
      var sizes = [], alphas = [];
      kept.forEach(function (c) {
        var k = Math.sqrt(c.count) / Math.sqrt(max); // 0..1
        sizes.push(3.5 + 4.5 * k);                   // 3.5..8 CSS px
        alphas.push(0.5 + 0.45 * k);                 // 0.5..0.95
      });
      var g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(cp, 3));
      g.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 1));
      g.setAttribute('aAlpha', new THREE.Float32BufferAttribute(alphas, 1));
      g.computeBoundingSphere();
      // A new geometry per poll, the old one disposed, so its GPU buffers are freed.
      var old = cityGeo; cityGeo = g; cityPoints.geometry = g; if (old) old.dispose();
      requestRender();
    }
    function applyGlobeTheme() {
      if (!globe) return;
      colors = themeColors();
      sphereMat.color.copy(colors.ocean);
      var col = landGeo.getAttribute('color'), t = landGeo.userData.t, tmp = new THREE.Color();
      for (var i = 0; i < t.length; i++) { tmp.copy(colors.land).lerp(colors.landDim, Math.min(1, t[i] * t[i] * 1.2)); col.setXYZ(i, tmp.r, tmp.g, tmp.b); }
      col.needsUpdate = true;
      cityMat.uniforms.uColor.value.copy(colors.city);
      syncDotSize();
      marks.forEach(function (m) { m.setColor(colors[m.type]); if (m.curve) m.buildTube(); });
      requestRender();
    }
    function resize() {
      if (!renderer) return;
      // Measure the canvas box, not the stage: on phones the canvas can sit below a stats strip.
      // On desktop the canvas fills the stage (inset 0), so both are the same size.
      W = canvasHost.clientWidth; H = canvasHost.clientHeight;
      if (!W || !H) return;
      offX = canvasHost.offsetLeft; offY = canvasHost.offsetTop;
      fill = parseFloat(tok('--hg-globe-fill')) || 0.88;
      cardTop = parseFloat(tok('--hg-card-top')); if (!(cardTop >= 0)) cardTop = 58;
      cardBottom = parseFloat(tok('--hg-card-bottom')); if (!(cardBottom >= 0)) cardBottom = 66;
      renderer.setSize(W, H, false);
      camera.aspect = W / H;
      var halfV = Math.tan((camera.fov / 2) * Math.PI / 180);
      var targetH = (Math.min(W, H) * fill) / H; // globe diameter fills 88% of the smaller side (mockup; phones may set --hg-globe-fill)
      camera.position.set(0, 0, (R * 2) / (targetH * 2 * halfV));
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      syncDotSize();
      if (cityMat) cityMat.uniforms.uScale.value = Math.max(0.7, Math.min(1.25, H / 620));
      requestRender();
    }

    // ---------------------------------------------------------- marks + arcs
    // Arcs: lift capped at 0.16, none over 90 degrees (that hop has no line), and clipped
    // to the globe's on-screen disc so no arc leaves the outline.
    var ARC_MAX_DEG = 90, ARC_MAX_LIFT = 0.16;
    var marks = [], simTime = 0, calm = reduced, paused = reduced, camLocal = THREE ? new THREE.Vector3() : null;
    function insideDisc(pt) {
      var D = camLocal.length(), vx = pt.x - camLocal.x, vy = pt.y - camLocal.y, vz = pt.z - camLocal.z;
      var vl = Math.sqrt(vx * vx + vy * vy + vz * vz);
      var cosA = -(vx * camLocal.x + vy * camLocal.y + vz * camLocal.z) / (vl * D);
      return cosA >= Math.sqrt(1 - (R * R) / (D * D)) + 0.0006;
    }
    function Mark(stop, fromV) {
      var self = this; this.type = stop.type; this.born = simTime;
      var col = colors[stop.type] || colors.page_view;
      var p = ll2v(stop.lat, stop.lng, R * 1.006);
      this.dotMat = new THREE.MeshBasicMaterial({ color: col, transparent: true });
      this.dot = new THREE.Mesh(new THREE.CircleGeometry(stop.type === 'purchase' ? 0.024 : 0.018, 24), this.dotMat);
      this.dot.position.copy(p); this.dot.lookAt(p.clone().multiplyScalar(2));
      this.ringMat = new THREE.MeshBasicMaterial({ map: RING_TEX, color: col, transparent: true, depthWrite: false });
      this.ring = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.16), this.ringMat);
      this.ring.position.copy(ll2v(stop.lat, stop.lng, R * 1.007)); this.ring.lookAt(p.clone().multiplyScalar(2));
      this.ring.visible = false; this.dot.visible = false;
      globe.add(this.dot); globe.add(this.ring);
      var a0 = fromV ? fromV.clone().normalize() : null, b0 = p.clone().normalize();
      var ang = a0 ? a0.angleTo(b0) : 0;
      if (a0 && ang > 0.01 && ang <= ARC_MAX_DEG * Math.PI / 180) {
        var lift = Math.min(0.03 + ang * 0.12, ARC_MAX_LIFT), pts = [], sinA = Math.sin(ang);
        for (var k = 0; k <= 48; k++) {
          var t = k / 48, w1 = Math.sin((1 - t) * ang) / sinA, w2 = Math.sin(t * ang) / sinA;
          var q = a0.clone().multiplyScalar(w1).add(b0.clone().multiplyScalar(w2)).normalize();
          pts.push(q.multiplyScalar(R * (1.004 + lift * Math.sin(Math.PI * t))));
        }
        this.curve = new THREE.CatmullRomCurve3(pts);
        this.arcSegs = 72; this.arcRad = 6;
        this.arcN = []; this.arcP = [];
        for (var j = 0; j <= this.arcSegs; j++) {
          var ap = this.curve.getPointAt(j / this.arcSegs); this.arcP.push(ap.clone()); this.arcN.push(ap.normalize());
        }
        this.arcMat = new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.95, depthTest: true });
        this.buildTube();
      }
      this.setColor = function (c) { if (!c) return; self.dotMat.color.copy(c); self.ringMat.color.copy(c); if (self.arcMat) self.arcMat.color.copy(c); };
    }
    // (Re)build the arc tube at the current look's width. Called on creation and on a theme switch.
    Mark.prototype.buildTube = function () {
      var range = this.arc ? { s: this.arc.geometry.drawRange.start, c: this.arc.geometry.drawRange.count, v: this.arc.visible } : { s: 0, c: 0, v: true };
      if (this.arc) { globe.remove(this.arc); this.arc.geometry.dispose(); }
      var tg = new THREE.TubeGeometry(this.curve, this.arcSegs, 0.0055 * colors.arcWidth, this.arcRad, false);
      this.arc = new THREE.Mesh(tg, this.arcMat);
      tg.setDrawRange(range.s, range.c); this.arc.visible = range.v;
      globe.add(this.arc);
    };
    Mark.prototype.update = function (now, arrive) {
      var age = now - this.born;
      if (this.arc) {
        var p = calm ? 1 : Math.min(1, age / 1000);
        p = 1 - Math.pow(1 - p, 3);
        var last = Math.floor(p * this.arcSegs), per = this.arcRad * 6;
        var horizon = R / camLocal.length() + 0.02, c = camLocal.clone().normalize();
        var j0 = -1, j1 = -1;
        for (var j = 0; j < last; j++) {
          // keep only the visible run nearest the arrival end, so a clipped middle is never drawn
          var ok = this.arcN[j].dot(c) > horizon && this.arcN[j + 1].dot(c) > horizon && insideDisc(this.arcP[j]) && insideDisc(this.arcP[j + 1]);
          if (ok) { if (j0 < 0 || j1 !== j - 1) j0 = j; j1 = j; }
        }
        if (j0 < 0) this.arc.geometry.setDrawRange(0, 0);
        else this.arc.geometry.setDrawRange(j0 * per, (j1 - j0 + 1) * per);
      }
      var landed = age >= arrive;
      this.dot.visible = landed;
      if (landed && !calm) {
        var rp = ((age - arrive) % 1800) / 1800;
        this.ring.visible = age - arrive < 5400;
        this.ring.scale.setScalar(0.25 + rp * 1.1);
        this.ringMat.opacity = (1 - rp) * 0.9;
      } else this.ring.visible = false;
    };
    Mark.prototype.fade = function (o) {
      this.dotMat.opacity = Math.max(0.35, o);
      if (this.arcMat) this.arcMat.opacity = 0.95 * o;
      if (this.arc) this.arc.visible = o > 0.02;
    };
    Mark.prototype.dispose = function () {
      [this.dot, this.ring, this.arc].forEach(function (m) { if (!m) return; globe.remove(m); m.geometry.dispose(); if (m.material) m.material.dispose(); });
    };

    // ------------------------------------------------------------ camera + card
    var rotY = 0, rotX = 0, fromY = 0, fromX = 0, toY = 0, toX = 0, easeStart = -1, EASE = 1200;
    function aim(lat, lng) {
      toY = -lng * Math.PI / 180;
      toX = Math.max(-0.75, Math.min(0.75, (lat - 6) * Math.PI / 180));
      var d = toY - rotY; d = ((d + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI; toY = rotY + d;
      fromY = rotY; fromX = rotX; easeStart = simTime;
    }
    function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
    var current = null, currentV = null, cardW = 0, cardH = 0, offX = 0, offY = 0, fill = 0.88, cardTop = 58, cardBottom = 66;
    function showCard(stop) {
      if (!card) return;
      cardCity.textContent = stopLabel(stop); // every stop shows its city only (founder 2026-10-07)
      card.classList.add('show');
      cardW = card.offsetWidth; cardH = card.offsetHeight; // measured once per text change, not per frame
    }
    function placeCard() {
      if (!card || !currentV || !card.classList.contains('show')) return;
      var w = currentV.clone().applyMatrix4(globe.matrixWorld);
      var facing = w.clone().normalize().dot(camera.position.clone().sub(w).normalize());
      if (facing < 0.1) { card.style.opacity = '0'; return; } else card.style.opacity = '';
      var s = w.project(camera);
      var sx = (s.x + 1) / 2 * W, sy = (1 - s.y) / 2 * H;
      var cw = cardW, ch = cardH, m = 12;
      var x = sx - cw - 18, y = sy + 10;
      if (x < m) x = sx + 18;
      x = Math.max(m, Math.min(W - cw - m, x));
      y = Math.max(cardTop, Math.min(H - ch - cardBottom, y));
      card.style.left = (offX + x) + 'px'; card.style.top = (offY + y) + 'px';
    }

    // ------------------------------------------------------------------ the loop
    var HOP = 3600, ARRIVE = 1150, lastFrame = 0, nextHop = 0, needRender = true, rafId = 0, hopTimer = 0, tourOn = false, calmTick = false;
    function kick() { if (!rafId && renderer && !document.hidden) rafId = requestAnimationFrame(frame); }
    function requestRender() { needRender = true; kick(); }
    function hop() {
      var stop = picker.next();
      if (!stop) return false;
      var fromV = current ? ll2v(current.lat, current.lng) : null;
      current = stop; currentV = ll2v(stop.lat, stop.lng, R * 1.006);
      if (card) card.classList.remove('show');
      var mk = new Mark(stop, fromV); mk.stop = stop; mk.shown = false;
      marks.push(mk);
      while (marks.length > 9) marks.shift().dispose();
      aim(stop.lat, stop.lng);
      if (calm) { rotY = toY; rotX = toX; easeStart = -1; }
      return true;
    }
    function frame(now) {
      rafId = 0;
      var dt = lastFrame ? Math.min(64, now - lastFrame) : 16; lastFrame = now;
      if (!paused && tourOn) {
        // Reduced motion: stops advance only on the hop timer's tick, never on other redraws.
        if (calm) { if (calmTick) simTime += HOP; } else simTime += dt;
        calmTick = false;
        if (simTime >= nextHop) { hop(); nextHop = simTime + HOP; }
        if (easeStart >= 0 && !calm) {
          var p = Math.min(1, (simTime - easeStart) / EASE), e = easeInOut(p);
          rotY = fromY + (toY - fromY) * e; rotX = fromX + (toX - fromX) * e;
          if (p >= 1) easeStart = -1;
        } else if (!calm) {
          rotY += dt * 0.000035; // slow drift between hops
        }
        needRender = true;
      }
      if (globe) {
        globe.rotation.set(rotX, rotY, 0, 'XYZ'); globe.updateMatrixWorld();
        camLocal.copy(camera.position); globe.worldToLocal(camLocal);
        marks.forEach(function (m, i) {
          m.update(simTime, calm ? 0 : ARRIVE);
          var rank = marks.length - 1 - i;
          m.fade(rank === 0 ? 1 : Math.max(0, 1 - rank * 0.34));
        });
        var latest = marks[marks.length - 1];
        if (latest && !latest.shown && simTime - latest.born >= (calm ? 0 : ARRIVE)) {
          latest.shown = true; showCard(latest.stop); announce(latest.stop);
        }
      }
      if (globe && needRender) {
        renderer.render(scene, camera);
        hideLoader(); // the first real frame replaces the loading ring
        placeCard();
        needRender = false;
      }
      // Loop only while playing and visible. Reduced motion (after play is pressed): one wake per hop,
      // and a redraw in between neither advances nor reschedules it.
      if (!paused && tourOn && !document.hidden) {
        if (calm) { if (!hopTimer) hopTimer = setTimeout(function () { hopTimer = 0; calmTick = true; requestRender(); }, HOP); }
        else kick();
      } else { clearTimeout(hopTimer); hopTimer = 0; }
    }
    function startTour() {
      if (!webglOk || tourOn) return;
      tourOn = true;
      // First stop is shown straight away (also the only stop with reduced motion, until play).
      if (hop()) {
        rotY = toY; rotX = toX; easeStart = -1;
        var mk0 = marks[marks.length - 1];
        mk0.born = -ARRIVE;
      }
      nextHop = simTime + HOP;
      requestRender();
    }
    document.addEventListener('visibilitychange', function () {
      lastFrame = 0;
      if (!document.hidden) requestRender();
    });

    // -------------------------------------------------------------- pause button
    function syncPlay() {
      if (!playBtn) return;
      playBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      var ip = playBtn.querySelector('[data-icon-pause]'), il = playBtn.querySelector('[data-icon-play]');
      if (ip) ip.style.display = paused ? 'none' : '';
      if (il) il.style.display = paused ? '' : 'none';
      if (pulseEl) { pulseEl.classList.toggle('is-beat', !paused && !reduced); pulseEl.classList.toggle('is-paused', paused); }
    }
    if (playBtn) {
      playBtn.addEventListener('click', function () {
        paused = !paused; lastFrame = 0; syncPlay();
        if (!paused) requestRender();
      });
    }
    if (mqReduce.addEventListener) {
      mqReduce.addEventListener('change', function () {
        reduced = !!mqReduce.matches; calm = reduced;
        if (reduced) paused = true;
        sectionEl.classList.toggle('is-reduced', reduced);
        syncPlay(); requestRender();
      });
    }
    sectionEl.classList.toggle('is-reduced', reduced);
    syncPlay();

    if (webglOk) {
      buildScene();
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(stage);
      else window.addEventListener('resize', resize);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestRender);
      requestRender();
    }

    // ================================================================= Chatroom
    function initChat() {
      var list = $('[data-hgv2-chat-list]');
      var countEl = $('[data-hgv2-chat-count]');
      var liveEl = $('[data-hgv2-chat-live]');
      var form = $('[data-hgv2-composer]');
      if (!list) return;
      var msgIn = $('[data-hgv2-msg]'), igIn = $('[data-hgv2-ig]'), cityIn = $('[data-hgv2-city]');
      var errEl = $('[data-hgv2-err]'), sendBtn = $('[data-hgv2-send]'), creditEl = $('[data-hgv2-credit]');
      var sugBox = $('[data-hgv2-sug]');
      var shown = {}, publicCount = 0, pendingCount = 0, stick = true;

      function syncCount() {
        if (!countEl) return;
        var s = String(T.messages || '[n] messages').replace('[n]', publicCount);
        if (pendingCount) s += ' · ' + String(T.inReviewCount || '[n] in review').replace('[n]', pendingCount);
        countEl.textContent = s;
      }
      function toBottom() { list.scrollTop = list.scrollHeight; stick = true; }
      list.addEventListener('scroll', function () { stick = list.scrollHeight - list.scrollTop - list.clientHeight < 24; });
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(function () { if (stick) list.scrollTop = list.scrollHeight; }).observe(list);

      function bubble(n, mine) {
        // User text goes in with textContent only, never innerHTML.
        var li = document.createElement('li'); li.className = 'hgv2-note' + (mine ? ' is-mine' : '');
        var b = document.createElement('div'); b.className = 'hgv2-note__bubble';
        var p = document.createElement('p'); p.className = 'hgv2-note__msg'; p.textContent = n.message || '';
        b.appendChild(p); li.appendChild(b);
        var meta = document.createElement('div'); meta.className = 'hgv2-note__meta';
        function sep() { var s = document.createElement('span'); s.className = 'hgv2-note__sep'; s.setAttribute('aria-hidden', 'true'); s.textContent = '·'; meta.appendChild(s); }
        if (mine) { var pend = document.createElement('span'); pend.className = 'hgv2-note__pending'; pend.textContent = T.inReview || 'In review'; meta.appendChild(pend); }
        var h = String(n.instagram || '').trim().replace(/^@+/, '');
        if (h && /^[A-Za-z0-9._]{1,30}$/.test(h)) {
          var a = document.createElement('a');
          a.href = 'https://www.instagram.com/' + encodeURIComponent(h) + '/';
          a.target = '_blank'; a.rel = 'noopener noreferrer'; a.textContent = '@' + h;
          a.setAttribute('aria-label', String(T.igLink || '@[h] on Instagram (opens in a new tab)').replace('[h]', h));
          meta.appendChild(a); sep();
        }
        var loc = n.location_label || n.location || '';
        if (loc) { var l = document.createElement('span'); l.textContent = loc; meta.appendChild(l); sep(); }
        if (n.created_at) {
          var t = document.createElement('time'); t.dateTime = n.created_at; t.textContent = relTime(n.created_at);
          meta.appendChild(t);
        } else if (meta.lastChild && meta.lastChild.className === 'hgv2-note__sep') meta.removeChild(meta.lastChild);
        li.appendChild(meta);
        return li;
      }
      function addPublic(notes) {
        // newest at the bottom
        notes.slice().sort(function (a, b) { return String(a.created_at).localeCompare(String(b.created_at)); }).forEach(function (n) {
          if (!n || !n.id || shown[n.id] || isStaffHandle(n.instagram)) return;
          shown[n.id] = true; publicCount++;
          list.appendChild(bubble(n, false));
        });
      }
      // The posting browser keeps its own pending messages (same storage keys as before).
      function getClientId() {
        try {
          var id = localStorage.getItem('haki_globe_client_id');
          if (!id) {
            id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
              : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
              });
            localStorage.setItem('haki_globe_client_id', id);
          }
          return id;
        } catch (e) { return null; }
      }
      function getMine() { try { return JSON.parse(localStorage.getItem('haki_globe_my_notes') || '[]'); } catch (e) { return []; } }
      function setMine(n) { try { localStorage.setItem('haki_globe_my_notes', JSON.stringify(n.slice(-20))); } catch (e) {} }
      function addMine(n) {
        if (!n || !n.id || shown[n.id]) return;
        shown[n.id] = true; pendingCount++;
        list.appendChild(bubble(n, true));
      }

      syncCount();
      if (API) {
        fetch(API + '/api/notes')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            addPublic((data && data.notes) || []);
            var mine = getMine();
            if (!mine.length) { syncCount(); toBottom(); return; }
            // Approved ones are already in the public list; rejected ones are dropped quietly.
            return fetch(API + '/api/notes/status?ids=' + mine.map(function (n) { return n.id; }).join(','))
              .then(function (r) { return r.json(); })
              .then(function (st) {
                var statuses = (st && st.statuses) || {}, keep = [];
                mine.forEach(function (n) {
                  var s = statuses[n.id] || 'pending';
                  if (s === 'rejected' || s === 'approved') return;
                  keep.push(n); addMine(n);
                });
                setMine(keep);
              })
              .catch(function () { mine.forEach(addMine); })
              .then(function () { syncCount(); toBottom(); });
          })
          .catch(function () { syncCount(); });
      }

      if (!form || !API) return;
      // --- city autocomplete (the API's /api/places, as before) ---
      var selectedPlace = null, sugItems = [], sugHl = -1, sugTimer = null, sugSeq = 0;
      function hideSug() {
        if (!sugBox) return;
        sugBox.hidden = true; sugBox.textContent = '';
        cityIn.setAttribute('aria-expanded', 'false'); cityIn.removeAttribute('aria-activedescendant');
        sugItems = []; sugHl = -1;
      }
      function pickSug(s) { selectedPlace = s; cityIn.value = s.label; hideSug(); }
      function renderSug(places) {
        sugBox.textContent = ''; sugItems = []; sugHl = -1; cityIn.removeAttribute('aria-activedescendant');
        if (!places.length) { hideSug(); return; }
        places.slice(0, 6).forEach(function (s, i) {
          var item = document.createElement('div');
          item.className = 'hgv2-sug__item'; item.id = sugBox.id + '-opt-' + i;
          item.setAttribute('role', 'option'); item.setAttribute('aria-selected', 'false');
          item.textContent = s.label;
          item.addEventListener('mousedown', function (e) { e.preventDefault(); pickSug(s); });
          sugBox.appendChild(item); sugItems.push({ el: item, place: s });
        });
        sugBox.hidden = false; cityIn.setAttribute('aria-expanded', 'true');
      }
      if (cityIn && sugBox) {
        cityIn.addEventListener('input', function () {
          selectedPlace = null; clearTimeout(sugTimer);
          var q = cityIn.value.trim();
          if (q.length < 3) { hideSug(); return; }
          sugTimer = setTimeout(function () {
            var s = ++sugSeq;
            fetch(API + '/api/places?q=' + encodeURIComponent(q))
              .then(function (r) { return r.json(); })
              .then(function (d) { if (s === sugSeq && document.activeElement === cityIn) renderSug((d && d.places) || []); })
              .catch(hideSug);
          }, 350);
        });
        // Suggestions only while the field has focus: a late /api/places answer after blur is dropped.
        cityIn.addEventListener('blur', function () { clearTimeout(sugTimer); ++sugSeq; hideSug(); });
        cityIn.addEventListener('keydown', function (e) {
          if (!sugItems.length) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            sugHl = e.key === 'ArrowDown' ? (sugHl + 1) % sugItems.length : (sugHl - 1 + sugItems.length) % sugItems.length;
            sugItems.forEach(function (it, i) { it.el.classList.toggle('is-hl', i === sugHl); it.el.setAttribute('aria-selected', i === sugHl ? 'true' : 'false'); });
            cityIn.setAttribute('aria-activedescendant', sugItems[sugHl].el.id);
          } else if (e.key === 'Enter' && sugHl >= 0) { e.preventDefault(); pickSug(sugItems[sugHl].place); }
          else if (e.key === 'Escape') { e.stopPropagation(); hideSug(); }
        });
      }

      // Errors: the message is linked to its field (aria-describedby) while it shows, and focus goes
      // to that field, or to Send when the server or network failed.
      var baseDesc = {};
      [msgIn, igIn, cityIn].forEach(function (f) { if (f) baseDesc[f.id] = f.getAttribute('aria-describedby') || ''; });
      function showErr(text, field) {
        errEl.textContent = text; errEl.hidden = false;
        var target = field || sendBtn;
        if (field) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', (errEl.id + ' ' + baseDesc[field.id]).trim());
        }
        if (target) target.focus();
      }
      function clearErr() {
        errEl.hidden = true; errEl.textContent = '';
        [msgIn, igIn, cityIn].forEach(function (f) {
          if (!f) return;
          f.removeAttribute('aria-invalid');
          if (baseDesc[f.id]) f.setAttribute('aria-describedby', baseDesc[f.id]); else f.removeAttribute('aria-describedby');
        });
      }
      // In flight: aria-disabled, not `disabled`, so keyboard focus stays on Send.
      var sending = false;
      function setSending(on) { sending = on; if (on) sendBtn.setAttribute('aria-disabled', 'true'); else sendBtn.removeAttribute('aria-disabled'); }
      msgIn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
          e.preventDefault();
          if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      });
      // Loyalty credit for a logged-in member's message (unchanged contract, see the section).
      function claimCredit(noteId) {
        if (!CFG.creditEnabled || !noteId) return;
        try {
          fetch(CFG.creditUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ noteId: noteId, clientId: getClientId() }) })
            .then(function (r) { return r.json(); })
            .then(function (body) {
              if (CFG.loyaltyOn === true && body && body.credited && body.credits && creditEl) {
                creditEl.textContent = String(T.credit || '+[n] credits added to your rewards.').replace('[n]', body.credits);
                creditEl.hidden = false;
              }
            })
            .catch(function () {});
        } catch (e) {}
      }
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (sending) return;
        clearErr();
        var message = msgIn.value.trim();
        var instagram = igIn.value.trim().replace(/^@+/, '');
        var address = cityIn.value.trim();
        if (message.length < 2) return showErr(T.errEmpty || 'Type a message before sending.', msgIn);
        if (address.length < 2) return showErr(T.errCity || 'Add your city so we can place you on the map.', cityIn);
        if (instagram && !/^[A-Za-z0-9._]{1,30}$/.test(instagram)) return showErr(T.errIg || 'Instagram handles use only letters, numbers, dots and underscores (up to 30).', igIn);
        setSending(true);
        fetch(API + '/api/notes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: message,
            instagram: instagram || null,
            address: address,
            // exact coordinates when a suggestion was picked
            place: (selectedPlace && selectedPlace.label === address) ? selectedPlace : null,
            client_id: getClientId()
          })
        })
          .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
          .then(function (res) {
            setSending(false);
            if (!res.ok) return showErr((res.body && res.body.detail) || T.errGeneric || 'Something went wrong. Try again.', null);
            var note = { id: res.body.id, message: message, instagram: instagram || null, lat: res.body.lat, lng: res.body.lng, location: res.body.location, created_at: new Date().toISOString() };
            var mine = getMine(); mine.push(note); setMine(mine);
            addMine(note); syncCount(); toBottom();
            msgIn.value = ''; igIn.value = ''; cityIn.value = ''; selectedPlace = null;
            msgIn.focus(); // ready for the next message
            if (liveEl) {
              liveEl.textContent = '';
              setTimeout(function () { liveEl.textContent = T.posted || 'Your message is in review.'; }, 50);
            }
            claimCredit(res.body.id);
          })
          .catch(function () { setSending(false); showErr(T.errNetwork || 'Network error. Try again.', null); });
      });
    }
  }

  function boot() {
    var els = document.querySelectorAll('[data-hgv2]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window !== 'undefined' ? window : globalThis);
